import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { TunnelPrivacyId, TunnelProtocol } from 'vscode/vscode/vs/platform/tunnel/common/tunnel';
import { ITunnelService } from 'vscode/vscode/vs/platform/tunnel/common/tunnel.service';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { IBrowserWorkbenchEnvironmentService } from 'vscode/vscode/vs/workbench/services/environment/browser/environmentService.service';
import { IOpenerService } from 'vscode/vscode/vs/platform/opener/common/opener.service';
import { URI } from 'vscode/vscode/vs/base/common/uri';
import { IRemoteExplorerService } from 'vscode/vscode/vs/workbench/services/remote/common/remoteExplorerService.service';
import { ILogService } from 'vscode/vscode/vs/platform/log/common/log.service';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { forwardedPortsViewEnabled } from 'vscode/vscode/vs/workbench/services/remote/common/tunnelModel';

const _moduleId = "vs/workbench/contrib/remote/browser/tunnelFactory";
let TunnelFactoryContribution = class TunnelFactoryContribution extends Disposable {
    static { this.ID = 'workbench.contrib.tunnelFactory'; }
    constructor(tunnelService, environmentService, openerService, remoteExplorerService, logService, contextKeyService) {
        super();
        this.openerService = openerService;
        const tunnelFactory = environmentService.options?.tunnelProvider?.tunnelFactory;
        if (tunnelFactory) {
            contextKeyService.createKey(forwardedPortsViewEnabled.key, true);
            let privacyOptions = environmentService.options?.tunnelProvider?.features?.privacyOptions ?? [];
            if (environmentService.options?.tunnelProvider?.features?.public
                && (privacyOptions.length === 0)) {
                privacyOptions = [
                    {
                        id: 'private',
                        label: ( localizeWithPath(_moduleId, 0, "Private")),
                        themeIcon: 'lock'
                    },
                    {
                        id: 'public',
                        label: ( localizeWithPath(_moduleId, 1, "Public")),
                        themeIcon: 'eye'
                    }
                ];
            }
            this._register(tunnelService.setTunnelProvider({
                forwardPort: async (tunnelOptions, tunnelCreationOptions) => {
                    let tunnelPromise;
                    try {
                        tunnelPromise = tunnelFactory(tunnelOptions, tunnelCreationOptions);
                    }
                    catch (e) {
                        logService.trace('tunnelFactory: tunnel provider error');
                    }
                    if (!tunnelPromise) {
                        return undefined;
                    }
                    let tunnel;
                    try {
                        tunnel = await tunnelPromise;
                    }
                    catch (e) {
                        logService.trace('tunnelFactory: tunnel provider promise error');
                        if (e instanceof Error) {
                            return e.message;
                        }
                        return undefined;
                    }
                    const localAddress = tunnel.localAddress.startsWith('http') ? tunnel.localAddress : `http://${tunnel.localAddress}`;
                    const remoteTunnel = {
                        tunnelRemotePort: tunnel.remoteAddress.port,
                        tunnelRemoteHost: tunnel.remoteAddress.host,
                        localAddress: await this.resolveExternalUri(localAddress),
                        privacy: tunnel.privacy ?? (tunnel.public ? TunnelPrivacyId.Public : TunnelPrivacyId.Private),
                        protocol: tunnel.protocol ?? TunnelProtocol.Http,
                        dispose: async () => { await tunnel.dispose(); }
                    };
                    return remoteTunnel;
                }
            }));
            const tunnelInformation = environmentService.options?.tunnelProvider?.features ?
                {
                    features: {
                        elevation: !!environmentService.options?.tunnelProvider?.features?.elevation,
                        public: !!environmentService.options?.tunnelProvider?.features?.public,
                        privacyOptions,
                        protocol: environmentService.options?.tunnelProvider?.features?.protocol === undefined ? true : !!environmentService.options?.tunnelProvider?.features?.protocol
                    }
                } : undefined;
            remoteExplorerService.setTunnelInformation(tunnelInformation);
        }
    }
    async resolveExternalUri(uri) {
        try {
            return (
                 ((await this.openerService.resolveExternalUri(( (URI.parse(uri))))).resolved.toString())
            );
        }
        catch {
            return uri;
        }
    }
};
TunnelFactoryContribution = ( (__decorate([
    ( (__param(0, ITunnelService))),
    ( (__param(1, IBrowserWorkbenchEnvironmentService))),
    ( (__param(2, IOpenerService))),
    ( (__param(3, IRemoteExplorerService))),
    ( (__param(4, ILogService))),
    ( (__param(5, IContextKeyService)))
], TunnelFactoryContribution)));

export { TunnelFactoryContribution };
